/**
 * Floating chat bubble — visible on every marketing page (mounted in
 * SiteLayout). Persists the current session_id + visitor identity in
 * localStorage so a page refresh keeps the conversation open.
 *
 * Flow:
 *   1. Anonymous state: pill button in the bottom-right.
 *   2. Click → panel opens. First-time visitors see a small name+email
 *      form (required per product decision 4b). Returning visitors skip
 *      straight to the message list.
 *   3. Every message the visitor sends triggers the backend to run the
 *      AI reply pipeline (unless a human has already taken over — the
 *      panel shows an "AtlasForge team is live in this chat" pill).
 *   4. Poll every 3s while the panel is open for new admin replies.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MessageCircle, X, Send, Loader2, CheckCircle2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";
const STORAGE_KEY = "atlasforge_chat_v1";

function loadStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveStored(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* private mode — session becomes ephemeral, which is fine */
  }
}

async function jsonFetch(path, opts = {}) {
  const res = await fetch(`${API}${path}`, {
    ...opts,
    headers: { "Content-Type": "application/json", ...(opts.headers || {}) },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export default function ChatBubble() {
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation("chat");
  // Hide the bubble on admin routes — admins already have the inbox panel
  // inside AdminDashboard and don't need to chat with themselves.
  const isAdminRoute = /(^|\/)admin(\/|$)/.test(pathname);
  const stored = loadStored();
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(stored);
  const [messages, setMessages] = useState([]);
  const [humanTaken, setHumanTaken] = useState(false);
  const [name, setName] = useState(stored?.name || "");
  const [email, setEmail] = useState(stored?.email || "");
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const scrollerRef = useRef(null);
  const pollTimerRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, []);

  const pollMessages = useCallback(async () => {
    if (!session?.session_id) return;
    try {
      const data = await jsonFetch(`/api/chat/sessions/${session.session_id}/messages`);
      setMessages(data.messages || []);
      setHumanTaken(Boolean(data.human_taken_over));
    } catch (e) {
      // Session may have been purged (TTL). Silently reset so the visitor
      // can start fresh instead of seeing a broken panel.
      if (String(e).includes("HTTP 404")) {
        localStorage.removeItem(STORAGE_KEY);
        setSession(null);
        setMessages([]);
      }
    }
  }, [session?.session_id]);

  // Poll every 3s while the panel is open — gives near-realtime UX
  // without the complexity of WebSockets and works cleanly behind CDNs.
  useEffect(() => {
    if (!open || !session?.session_id) {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
      return () => {};
    }
    pollMessages();
    pollTimerRef.current = setInterval(pollMessages, 3000);
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [open, session?.session_id, pollMessages]);

  useEffect(() => {
    if (open) scrollToBottom();
  }, [messages, open, scrollToBottom]);

  const startSession = async (initialMessage) => {
    setError("");
    if (!name.trim() || !email.trim() || !email.includes("@")) {
      setError(t("errors.missingFields"));
      return;
    }
    setBusy(true);
    try {
      const data = await jsonFetch("/api/chat/sessions", {
        method: "POST",
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          initial_message: initialMessage || null,
          // Locale tells the backend which language the AI should reply in.
          // We pass i18n.language rather than the URL prefix so a visitor
          // who explicitly changed the language via the switcher (without
          // triggering a route change yet) still gets a matching AI reply.
          locale: (i18n.language || "en").split("-")[0],
        }),
      });
      const record = {
        session_id: data.session_id,
        name: name.trim(),
        email: email.trim(),
      };
      saveStored(record);
      setSession(record);
      await pollMessages();
    } catch (e) {
      setError(t("errors.startFailed"));
    } finally {
      setBusy(false);
    }
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending) return;
    if (!session?.session_id) {
      await startSession(text);
      setInput("");
      return;
    }
    setSending(true);
    // Optimistic append so the message shows instantly.
    setMessages((prev) => [
      ...prev,
      { id: `local-${Date.now()}`, role: "visitor", text, created_at: new Date().toISOString() },
    ]);
    setInput("");
    try {
      await jsonFetch(`/api/chat/sessions/${session.session_id}/messages`, {
        method: "POST",
        body: JSON.stringify({
          text,
          locale: (i18n.language || "en").split("-")[0],
        }),
      });
      // Rely on the polling loop to hydrate the AI/admin reply.
      pollMessages();
    } catch {
      setError(t("errors.sendFailed"));
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {isAdminRoute ? null : !open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="chat-bubble-open"
          aria-label={t("bubble.ariaOpen")}
          className="fixed bottom-5 right-5 z-50 inline-flex items-center gap-2 rounded-full bg-[#0A1128] text-white shadow-2xl hover:bg-[#1E293B] transition-all px-5 py-3.5"
        >
          <MessageCircle className="h-5 w-5" strokeWidth={2.2} />
          <span className="text-[14px] font-medium">{t("bubble.openLabel")}</span>
        </button>
      )}

      {!isAdminRoute && open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-5 right-5 z-50 w-[380px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-2.5rem)] rounded-2xl bg-white shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 bg-[#0A1128] text-white">
            <div className="flex items-center gap-2.5">
              <div className="h-2.5 w-2.5 rounded-full bg-emerald-400 animate-pulse" />
              <div>
                <p className="text-[14px] font-semibold leading-tight">{t("panel.title")}</p>
                <p className="text-[11.5px] text-slate-300 leading-tight">
                  {humanTaken ? t("panel.statusHuman") : t("panel.statusReady")}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="chat-bubble-close"
              className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
              aria-label={t("panel.closeAria")}
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Body */}
          {!session ? (
            <div className="flex-1 overflow-y-auto px-5 py-5">
              <p className="text-[14px] text-slate-700 leading-relaxed">
                {t("intro.welcome")}
              </p>
              <div className="mt-5 space-y-3">
                <div>
                  <label className="block text-[12px] uppercase tracking-wider text-slate-500 mb-1.5">{t("intro.nameLabel")}</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t("intro.namePlaceholder")}
                    data-testid="chat-input-name"
                    className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#0A1128]/40"
                  />
                </div>
                <div>
                  <label className="block text-[12px] uppercase tracking-wider text-slate-500 mb-1.5">{t("intro.emailLabel")}</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t("intro.emailPlaceholder")}
                    data-testid="chat-input-email"
                    className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#0A1128]/40"
                  />
                </div>
                {error && (
                  <p data-testid="chat-error" className="text-[12.5px] text-rose-600">{error}</p>
                )}
                <button
                  type="button"
                  onClick={() => startSession(null)}
                  disabled={busy}
                  data-testid="chat-start-session"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0A1128] text-white text-[14px] font-medium px-4 py-2.5 hover:bg-[#1E293B] transition-colors disabled:opacity-70"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  {t("intro.startButton")}
                </button>
                <p className="text-[11.5px] text-slate-500 leading-relaxed">
                  {t("intro.consent")}
                </p>
              </div>
            </div>
          ) : (
            <>
              <div
                ref={scrollerRef}
                className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#F7F9FC]"
                data-testid="chat-messages"
              >
                {messages.length === 0 && (
                  <div className="text-[13px] text-slate-500 text-center py-6">{t("loading")}</div>
                )}
                {messages.map((m) => (
                  <MessageBubble key={m.id} msg={m} t={t} />
                ))}
              </div>
              {error && (
                <div className="px-4 py-2 border-t border-rose-100 bg-rose-50">
                  <p data-testid="chat-error" className="text-[12.5px] text-rose-700">{error}</p>
                </div>
              )}
              <div className="border-t border-slate-200 bg-white p-3 flex gap-2 items-end">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder={t("input.placeholder")}
                  rows={1}
                  data-testid="chat-input-message"
                  className="flex-1 resize-none rounded-lg border border-slate-300 px-3 py-2 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-[#0A1128]/40 max-h-32"
                />
                <button
                  type="button"
                  onClick={sendMessage}
                  disabled={sending || !input.trim()}
                  data-testid="chat-send"
                  aria-label={t("input.sendAria")}
                  className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-[#0A1128] text-white hover:bg-[#1E293B] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}

function MessageBubble({ msg, t }) {
  const isVisitor = msg.role === "visitor";
  const isAdmin = msg.role === "admin";
  return (
    <div className={`flex ${isVisitor ? "justify-end" : "justify-start"}`}>
      <div
        data-testid={`chat-msg-${msg.role}`}
        className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-[13.5px] leading-relaxed whitespace-pre-wrap ${
          isVisitor
            ? "bg-[#0A1128] text-white rounded-br-sm"
            : isAdmin
            ? "bg-emerald-100 text-emerald-950 border border-emerald-200 rounded-bl-sm"
            : "bg-white text-slate-800 border border-slate-200 rounded-bl-sm"
        }`}
      >
        {!isVisitor && (
          <p className="text-[10.5px] uppercase tracking-wider mb-1 opacity-70">
            {isAdmin ? t("labels.team") : t("labels.assistant")}
          </p>
        )}
        {msg.text}
      </div>
    </div>
  );
}
